import type { ReactNode } from "react";
import { Link, useHistory } from "react-router-dom";
import { IconChevronLeft } from "@tabler/icons-react";
import { cn } from "../../lib/utils";

type BackButtonProps = {
  /** Link destination. When omitted, falls back to `onClick` or `history.goBack()`. */
  to?: string | { pathname: string; state?: unknown };
  /** Use `replace` instead of `push` for the Link navigation. */
  replace?: boolean;
  /** Custom click handler. Takes precedence over default `history.goBack()` when `to` is not set. */
  onClick?: () => void;
  /** Label displayed next to the icon. */
  children?: ReactNode;
  className?: string;
};

export function BackButton({
  to,
  replace: replaceProp,
  onClick,
  children,
  className,
}: BackButtonProps): React.ReactElement {
  const history = useHistory();

  const classes = cn(
    // Per Figma (node 198:13341): 24px icon box flush against the label — the
    // chevron's own 9px of in-box padding IS the spacing, no extra gap.
    "flex items-center text-[#212121] no-underline font-dm-sans text-ui-body font-normal cursor-pointer",
    className
  );

  const content = (
    <>
      <IconChevronLeft className="size-6!" />
      {children}
    </>
  );

  if (to != null) {
    return (
      <Link to={to} replace={replaceProp} className={classes}>
        {content}
      </Link>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      className={classes}
      onClick={() => {
        if (onClick) {
          onClick();
        } else {
          history.goBack();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          if (onClick) {
            onClick();
          } else {
            history.goBack();
          }
        }
      }}
    >
      {content}
    </div>
  );
}
